import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { addNote, assign, getItem, getPeople, setDue, setStatus, taskAdd, taskDone, taskNote, taskStart } from '../lib/api';
import type { Person } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { boardGroup, remainingGates, shortActor, shortDate, taskWho, timeAgo, versionsSummary } from '../lib/format';
import { Chip, DueChip, KindChip, PriorityChip, StatusPill } from '../components/Chips';
import { useAuth } from '../lib/auth';
import type { ItemDetail } from '../lib/types';

const STATUSES = ['open', 'planned', 'in_progress', 'built', 'fixed', 'released', 'verified', 'done', 'parked'];

export default function ItemPage() {
  const { key = '' } = useParams();
  const { role } = useAuth();
  const canEdit = role === 'developer';
  // Reporters may assign, set the due date, work the checklist and write notes;
  // status and the item itself stay with developers.
  const canWork = role === 'developer' || role === 'reporter';
  const { data, error, loading, reload } = useAsync<ItemDetail>(() => getItem(key), [key]);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [newTask, setNewTask] = useState('');
  const [people, setPeople] = useState<Person[]>([]);
  const [openTask, setOpenTask] = useState<string | null>(null);
  const [taskText, setTaskText] = useState('');

  useEffect(() => {
    getPeople().then((p) => setPeople(p.people)).catch(() => setPeople([]));
  }, []);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="loading">Loading {key}...</p>;
  if (error) return <div className="error">{error}</div>;
  if (!data) return null;

  const { item, tasks, gates, fixes, events, sightings, rollout, matrix } = data;
  const doneTasks = tasks.filter((t) => t.state === 'done' || t.state === 'dropped');
  const openTasks = tasks.filter((t) => t.state === 'todo' || t.state === 'doing');
  const pendingGates = remainingGates(item, gates);
  const doneGates = gates.filter((g) => g.state === 'done' || g.state === 'n/a');

  return (
    <>
      <p className="sub">
        <Link to="/">Board</Link> / <span className="key">{item.key}</span>
      </p>
      <div className="detail-head">
        <h1>{item.title}</h1>
        <StatusPill status={boardGroup(item)} />
        <KindChip kind={item.kind} />
        <PriorityChip priority={item.priority} />
        {item.severity ? <Chip>{item.severity}</Chip> : null}
        <DueChip due={item.due_date} />
      </div>
      {item.summary ? <p className="sub">{item.summary}</p> : null}
      <div className="detail-meta">
        <span>
          <b>Components</b> {item.components.length ? item.components.join(', ') : 'none'}
        </span>
        <span>
          <b>Owner</b>{' '}
          {canWork ? (
            <select
              style={{ width: 'auto', padding: '2px 6px' }}
              value={item.owner ?? ''}
              disabled={busy}
              onChange={(e) => act(() => assign(item.key, e.target.value || null))}
            >
              <option value="">unassigned</option>
              {people.map((p) => (
                <option key={p.email} value={p.email}>
                  {p.name}
                </option>
              ))}
              {item.owner && !people.some((p) => p.email === item.owner) ? (
                <option value={item.owner}>{item.owner}</option>
              ) : null}
            </select>
          ) : (
            item.owner ?? 'nobody'
          )}
        </span>
        <span>
          <b>Due</b>{' '}
          {canWork ? (
            <input
              type="date"
              style={{ width: 'auto', padding: '2px 6px' }}
              value={item.due_date ?? ''}
              disabled={busy}
              onChange={(e) => act(() => setDue(item.key, e.target.value || null))}
            />
          ) : (
            item.due_date ?? 'none'
          )}
        </span>
        <span>
          <b>Updated</b> {timeAgo(item.updated_at)}
        </span>
        {item.kb_page ? (
          <span>
            <b>Page</b> <Link to={`/kb/${item.kb_page}`}>{item.kb_page}</Link>
          </span>
        ) : null}
      </div>

      {actionError ? <div className="error">{actionError}</div> : null}

      <div className="cols">
        <div className="panel">
          <h2 style={{ marginTop: 0 }}>Remaining</h2>
          {!openTasks.length && !pendingGates.length ? (
            <p className="empty" style={{ padding: '4px 0' }}>
              Nothing left.
            </p>
          ) : (
            <ul className="checklist">
              {openTasks.map((t) => (
                <li key={t.id}>
                  <span
                    className={canWork ? 'box' : 'box gate'}
                    title={canWork ? 'Mark done' : 'Sign in to tick tasks'}
                    role={canWork ? 'button' : undefined}
                    tabIndex={canWork ? 0 : undefined}
                    onClick={() => canWork && !busy && act(() => taskDone(item.key, t.seq))}
                    onKeyDown={(e) => canWork && e.key === 'Enter' && !busy && act(() => taskDone(item.key, t.seq))}
                  />
                  <span style={{ flex: 1 }}>
                    {t.title}
                    <p className="sub">
                      task {t.seq}
                      {t.state === 'doing' ? ' · in progress' : ''}
                      {taskWho(t) ? ` · ${taskWho(t)}` : ''}
                      {' · '}
                      <a href="#" onClick={(e) => { e.preventDefault(); setOpenTask(openTask === t.id ? null : t.id); setTaskText(''); }}>
                        history{events.some((e) => e.task_id === t.id) ? ` (${events.filter((e) => e.task_id === t.id).length})` : ''}
                      </a>
                      {canWork && t.state === 'todo' ? (
                        <>
                          {' · '}
                          <a href="#" onClick={(e) => { e.preventDefault(); if (!busy) act(() => taskStart(item.key, t.seq)); }}>
                            start
                          </a>
                        </>
                      ) : null}
                    </p>
                    {openTask === t.id ? (
                      <div className="task-history">
                        <ul className="timeline">
                          {events.filter((e) => e.task_id === t.id).map((e) => (
                            <li key={e.seq}>
                              <span className="when">{shortDate(e.at)}</span>
                              <span>
                                {e.note} <span className="who"> — {shortActor(e.by)}</span>
                              </span>
                            </li>
                          ))}
                          {!events.some((e) => e.task_id === t.id) ? <li className="sub">No history yet.</li> : null}
                        </ul>
                        {canWork ? (
                          <div className="row" style={{ marginTop: 6 }}>
                            <input
                              type="text"
                              placeholder="Progress on this step"
                              value={taskText}
                              onChange={(e) => setTaskText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && taskText.trim() && !busy) {
                                  act(() => taskNote(item.key, t.seq, taskText.trim())).then(() => setTaskText(''));
                                }
                              }}
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </span>
                </li>
              ))}
              {pendingGates.map((g) => (
                <li key={g}>
                  <span className="box gate" title="Gates are set by the kb CLI" />
                  <span>
                    gate {g}
                    <p className="sub">derived from git and the databases</p>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="row" style={{ marginTop: 14, display: canWork ? undefined : 'none' }}>
            <input
              type="text"
              placeholder="Add a step"
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newTask.trim() && !busy) {
                  act(() => taskAdd(item.key, newTask.trim())).then(() => setNewTask(''));
                }
              }}
            />
            <button
              disabled={busy || !newTask.trim()}
              onClick={() => act(() => taskAdd(item.key, newTask.trim())).then(() => setNewTask(''))}
            >
              Add
            </button>
          </div>
        </div>

        <div className="panel">
          <h2 style={{ marginTop: 0 }}>Done</h2>
          {!doneTasks.length && !doneGates.length ? (
            <p className="empty" style={{ padding: '4px 0' }}>
              Nothing yet.
            </p>
          ) : (
            <ul className="checklist">
              {doneTasks.map((t) => (
                <li key={t.id}>
                  <span className="box done">✓</span>
                  <span className={t.state === 'dropped' ? 'dropped' : undefined}>
                    {t.title}
                    <p className="sub">
                      {taskWho(t) || `${t.state} ${shortDate(t.done_at)}`}
                      {t.note ? ` · ${t.note}` : ''}
                    </p>
                  </span>
                </li>
              ))}
              {doneGates.map((g) => (
                <li key={`${g.gate}-${g.channel}`}>
                  <span className="box done">✓</span>
                  <span>
                    gate {g.gate}
                    <p className="sub">
                      {g.state} {g.channel !== 'prod' ? `(${g.channel}) ` : ''}
                      {g.ref ?? ''}
                    </p>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {canWork ? (
        <>
      <h2>Change</h2>
      <div className="panel">
        <div className="row" style={{ display: canEdit ? undefined : 'none' }}>
          <label htmlFor="status" style={{ color: 'var(--muted)' }}>
            Status
          </label>
          <select
            id="status"
            style={{ width: 'auto' }}
            value={item.status}
            disabled={busy}
            onChange={(e) => act(() => setStatus(item.key, e.target.value))}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <span className="hint" style={{ margin: 0 }}>
            done and verified are refused while anything is left.
          </span>
        </div>
        <div style={{ marginTop: canEdit ? 14 : 0 }}>
          <textarea
            placeholder="Add a note to the timeline"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="row" style={{ marginTop: 8 }}>
            <button
              disabled={busy || !note.trim()}
              onClick={() => act(() => addNote(item.key, note.trim())).then(() => setNote(''))}
            >
              Add note
            </button>
            <span className="hint" style={{ margin: 0 }}>
              Notes added here land on the timeline. Use the kb CLI when it belongs on the page itself.
            </span>
          </div>
        </div>
      </div>
        </>
      ) : null}

      {fixes.length ? (
        <>
          <h2>Fix legs</h2>
          <div className="panel">
            <table>
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Version</th>
                  <th>Channel</th>
                  <th>Since</th>
                  <th>Fleet</th>
                </tr>
              </thead>
              <tbody>
                {fixes.map((f) => {
                  const r = rollout.find((x) => x.component === f.component);
                  return (
                    <tr key={f.id}>
                      <td>{f.component}</td>
                      <td>{f.version ?? 'unversioned'}</td>
                      <td>{f.channel}</td>
                      <td>{shortDate(f.fixed_at)}</td>
                      <td>
                        {r
                          ? `${r.rooms_on_fix} of ${r.rooms_managed} rooms, ${r.devices_on_fix} of ${r.devices_total} devices`
                          : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {matrix.length ? (
        <>
          <h2>Sightings per version</h2>
          <div className="panel">
            <table>
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Version</th>
                  <th className="num">Seen</th>
                  <th className="num">Before fix</th>
                  <th className="num">After fix</th>
                  <th className="num">Unknown</th>
                  <th>Last</th>
                </tr>
              </thead>
              <tbody>
                {matrix.map((m) => (
                  <tr key={`${m.component}-${m.version}`}>
                    <td>{m.component.startsWith('experience:') ? 'experience' : m.component}</td>
                    <td>{m.version}</td>
                    <td className="num">{m.sightings}</td>
                    <td className="num pre">{m.pre || ''}</td>
                    <td className="num post">{m.post || ''}</td>
                    <td className="num unknown">{m.unknown || ''}</td>
                    <td>{shortDate(m.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {sightings.length ? (
        <>
          <h2>Recent sightings</h2>
          <div className="panel">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Source</th>
                  <th>Versions</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {sightings.slice(0, 12).map((s) => (
                  <tr key={s.id}>
                    <td>{shortDate(s.observed_at)}</td>
                    <td>{s.source}</td>
                    <td>{versionsSummary(s.versions)}</td>
                    <td>{s.note ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      <h2>Timeline</h2>
      <div className="panel">
        {!events.length ? (
          <p className="empty" style={{ padding: 0 }}>
            Nothing recorded yet.
          </p>
        ) : (
          <ul className="timeline">
            {events.map((e) => (
              <li key={e.seq}>
                <span className="when">{shortDate(e.at)}</span>
                <span>
                  {e.kind === 'status'
                    ? `status ${e.from_status ?? ''} to ${e.to_status ?? ''}`
                    : e.kind === 'gate'
                      ? `gate ${e.gate ?? ''} ${e.note ?? ''} ${e.ref ?? ''}`
                      : `${e.kind}: ${e.note ?? e.ref ?? ''}`}
                  <span className="who"> {e.by ? ` — ${shortActor(e.by)}` : ''}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
